/**
 * Users API Hooks - Direct SDK usage with TanStack Query
 *
 * Admin and user management hooks using the generated SDK directly.
 */

import type {
  AdminUserUpdate,
  UserCreate,
  UserResponse,
} from "@/client/types.gen"
import { api } from "@/services/api"
import type { UserWithRoles } from "@/types/models"
import { unwrapStandardResponse } from "@/utils/apiResponse"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

export interface UserListParams {
  page?: number
  perPage?: number
  search?: string
}

/**
 * Fetch paginated list of users (admin only)
 */
export function useUsers(params: UserListParams = {}) {
  return useQuery({
    queryKey: ["users", params],
    queryFn: async (): Promise<UserResponse[]> => {
      const response = await api.admin.listUsersApiV1AdminUsersGet({
        page: params.page,
        perPage: params.perPage,
        search: params.search,
      })
      return (unwrapStandardResponse<UserResponse[]>(response) ??
        []) as UserResponse[]
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

/**
 * Create new user (admin only)
 */
export function useCreateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userData: {
      email: string
      full_name: string
      password: string
      is_active?: boolean
    }) => {
      const userCreate: UserCreate = {
        email: userData.email,
        full_name: userData.full_name,
        password: userData.password,
        is_active: userData.is_active ?? true,
      }
      const response = await api.admin.createUserApiV1AdminUsersPost({
        requestBody: userCreate,
      })
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
  })
}

/**
 * Update user (admin only)
 */
export function useUpdateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      userId,
      userData,
    }: {
      userId: string
      userData: {
        email?: string
        full_name?: string
        password?: string
        is_active?: boolean
      }
    }) => {
      const userUpdate: AdminUserUpdate = {
        email: userData.email,
        full_name: userData.full_name,
        is_active: userData.is_active,
      }
      const response = await api.admin.updateUserApiV1AdminUsersUserIdPut({
        userId,
        requestBody: userUpdate,
      })
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
  })
}

/**
 * Delete user (admin only)
 */
export function useDeleteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await api.admin.deleteUserApiV1AdminUsersUserIdDelete({
        userId,
      })
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
  })
}

/**
 * Activate user (admin only)
 */
export function useActivateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userId: string) => {
      const response =
        await api.admin.activateUserApiV1AdminUsersUserIdActivatePost({
          userId,
        })
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
  })
}

/**
 * Deactivate user (admin only)
 */
export function useDeactivateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userId: string) => {
      const response =
        await api.admin.deactivateUserApiV1AdminUsersUserIdDeactivatePost({
          userId,
        })
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
  })
}

/**
 * Update current user profile
 */
export function useUpdateCurrentUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userData: {
      email?: string
      full_name?: string
    }) => {
      const response = await api.users.updateCurrentUserApiV1UsersMePut({
        requestBody: userData,
      })
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] })
    },
  })
}

/**
 * Change current user password
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: async (passwordData: {
      current_password: string
      new_password: string
    }) => {
      const response =
        await api.users.changePasswordApiV1UsersMeChangePasswordPost({
          requestBody: passwordData,
        })
      return response
    },
  })
}

/**
 * Deactivate current user
 */
export function useDeactivateCurrentUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response =
        await api.users.deactivateCurrentUserApiV1UsersMeDeactivatePost()
      return response
    },
    onSuccess: () => {
      // Clear all cached data and redirect to login
      queryClient.clear()
    },
  })
}

/**
 * Fetch users with roles (admin only) - for role management UI
 */
export function useUsersWithRoles() {
  return useQuery({
    queryKey: ["users-with-roles"],
    queryFn: async (): Promise<UserWithRoles[]> => {
      const response = await api.instance.get(
        "/api/v1/admin/users-with-roles",
        {
          params: {
            page: 1,
            per_page: 100,
          },
        },
      )
      return (unwrapStandardResponse<UserWithRoles[]>(response.data) ??
        []) as UserWithRoles[]
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

/**
 * Role assignment actions - combined hook for role management
 */
export function useRoleAssignment() {
  const queryClient = useQueryClient()

  const assignRole = useMutation({
    mutationFn: async ({
      userId,
      roleId,
      assignment_reason = "Admin assignment",
    }: {
      userId: string
      roleId: string
      assignment_reason?: string
    }) => {
      const response =
        await api.admin.assignRoleToUserApiV1AdminUsersUserIdRolesPost({
          userId,
          requestBody: { role_id: roleId, assignment_reason },
        })
      return response
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["users", variables.userId, "roles"],
      })
      queryClient.invalidateQueries({ queryKey: ["users"] })
      queryClient.invalidateQueries({ queryKey: ["users-with-roles"] })
    },
  })

  const removeRole = useMutation({
    mutationFn: async ({
      userId,
      roleId,
    }: { userId: string; roleId: string }) => {
      await api.admin.removeRoleFromUserApiV1AdminUsersUserIdRolesRoleIdDelete({
        userId,
        roleId,
      })
      return { userId, roleId }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["users", data.userId, "roles"],
      })
      queryClient.invalidateQueries({ queryKey: ["users"] })
      queryClient.invalidateQueries({ queryKey: ["users-with-roles"] })
    },
  })

  return {
    assignRole: assignRole.mutateAsync,
    removeRole: removeRole.mutateAsync,
    isAssigning: assignRole.isPending,
    isRemoving: removeRole.isPending,
  }
}
